// Per-slide-type Designer JSON → SlideData converters.
// One function per slide type. Each is referenced by the corresponding
// SLIDE_REGISTRY entry's `designer.importer`. Lives in its own module so
// slideRegistry can import these without forming a runtime cycle with
// lessonImport (which dispatches via the registry).
import type {
  SlideData,
  TextSlideData,
  CoverSlideData,
  ImageSlideData,
  VideoSlideData,
  CodeSlideData,
  QuizSlideData,
  WebappSlideData,
  CanvaSlideData,
  ReflectionSlideData,
  LikertSlideData,
  SurveyChoiceSlideData,
  FreeTextSlideData,
  DropdownSlideData,
  RankingSlideData,
  MatchingSlideData,
  GeneratedInteractiveSlideData,
  DoorKey,
} from '../../components/course-builder/slides/slideTypes';
import { getDoor, DOORS } from '../doors/doors';
import type { DesignerSlide } from '../lessonImport';

// --- Text parsing helpers ---

// Normalise any HTML the brief carries (the schema allows HTML in `text`) down to
// plain text BEFORE we split into heading/body — block tags become line breaks,
// inline tags are stripped. Without this, a wrapper like "<p>…</p>" gets split
// across heading/body and re-escaped, leaking literal "</p>" into the slide.
function htmlToPlain(s: string): string {
  return (s || '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|li|ul|ol)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * A slide title is a label, not a sentence — FINAL_COPY_RULES asks for 2–5
 * words. Anything longer than this is body copy wearing a heading's type
 * size, and it overruns the slide, so it is never taken as the title.
 */
export const TITLE_MAX_WORDS = 8;

/**
 * Capacity of a plain Text slide. Must equal the `text-body` registry
 * entry's `maxWords`; used for the continuation slides that a cover's or
 * title slide's overflow lands on, where the registry cap in hand is the
 * cover's own.
 */
export const TEXT_SLIDE_MAX_WORDS = 115;

/** Import-time limits the registry hands each importer (`designer.maxWords`). */
export interface ImportLimits {
  maxWords?: number;
}

export function wordCount(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

/** "Short title. The rest of the line…" → head + rest; null if the line is one sentence. */
function splitFirstSentence(line: string): { head: string; rest: string } | null {
  const m = line.match(/^(.+?[.!?])\s+(.+)$/);
  return m ? { head: m[1].trim(), rest: m[2].trim() } : null;
}

/**
 * Pull the title off the front of a slide's text.
 *
 * The Designer writes "Title.\n\nBody…" or "Title. Body…\n\nMore body". The
 * old split took the whole first LINE whenever there was a newline, so a
 * multi-paragraph slide put its entire opening paragraph in the heading —
 * which is what every slide of a one-pass Write looked like. Now: a short
 * first sentence wins, then a short first line, and if the text opens with
 * neither the slide gets no heading rather than a paragraph-sized one.
 */
function splitHeadingBody(text: string): { heading: string; body: string } {
  const t = htmlToPlain(text).trim();
  if (!t) return { heading: '', body: '' };
  const nl = t.indexOf('\n');
  const firstLine = nl > -1 ? t.slice(0, nl).trim() : t;
  // Keeps the paragraph break, so the body's paragraphs survive the split.
  const afterLine = nl > -1 ? t.slice(nl) : '';
  const sentence = splitFirstSentence(firstLine);
  if (sentence && wordCount(sentence.head) <= TITLE_MAX_WORDS) {
    return { heading: sentence.head, body: (sentence.rest + afterLine).trim() };
  }
  if (wordCount(firstLine) <= TITLE_MAX_WORDS) {
    return { heading: firstLine, body: afterLine.trim() };
  }
  return { heading: '', body: t };
}

/** Sentences of one paragraph, grouped greedily into runs of at most `budget` words. */
function sentenceGroups(paragraph: string, budget: number): string[] {
  const sentences = paragraph.split(/(?<=[.!?]["'”’)]?)\s+/).filter(Boolean);
  const groups: string[] = [];
  let cur: string[] = [];
  let words = 0;
  for (const s of sentences) {
    const w = wordCount(s);
    if (cur.length && words + w > budget) {
      groups.push(cur.join(' '));
      cur = [];
      words = 0;
    }
    cur.push(s);
    words += w;
  }
  if (cur.length) groups.push(cur.join(' '));
  return groups;
}

/**
 * Split plain body text into pages of at most `budget` words, breaking at
 * paragraphs (sentences only inside a paragraph too long for one page) and
 * balancing the pages so the last one isn't a stranded line.
 */
export function paginateBody(body: string, budget: number): string[] {
  const t = body.trim();
  if (!t) return [''];
  const b = Math.max(budget, 20);
  const units: string[] = [];
  for (const p of t.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean)) {
    if (wordCount(p) <= b) units.push(p);
    else units.push(...sentenceGroups(p, b));
  }
  const total = units.reduce((n, u) => n + wordCount(u), 0);
  if (total <= b) return [units.join('\n\n')];
  const target = total / Math.ceil(total / b);
  const pages: string[][] = [];
  let cur: string[] = [];
  let words = 0;
  for (const u of units) {
    const w = wordCount(u);
    // Turn the page when this unit won't fit, or when most of it would sit
    // past the balanced target.
    if (cur.length && (words + w > b || words + w / 2 > target)) {
      pages.push(cur);
      cur = [];
      words = 0;
    }
    cur.push(u);
    words += w;
  }
  if (cur.length) pages.push(cur);
  return pages.map((p) => p.join('\n\n'));
}

/** Untitled Text slides carrying overflow — the reader takes them as a continuation. */
function continuationSlides(overflow: string): TextSlideData[] {
  if (!overflow.trim()) return [];
  return paginateBody(overflow, TEXT_SLIDE_MAX_WORDS).map((page) => ({
    template: 'text',
    layout: 'body',
    heading: '',
    body: wrapParagraph(page),
  }));
}

function oneOrMany<T extends SlideData>(slides: T[]): T | T[] {
  return slides.length === 1 ? slides[0] : slides;
}

function wrapParagraph(text: string): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('<')) return trimmed;
  return trimmed
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Title + subtitle for a cover or title slide, plus whatever doesn't belong
 * on one. Only the first two lines are candidates: a cover is a title and a
 * line under it, so further paragraphs come back as `overflow` for the
 * caller to put on a slide of their own instead of a one-line subtitle field.
 */
function splitCoverTitle(text: string): { title: string; subtitle: string; overflow: string } {
  const t = htmlToPlain(text).trim();
  if (!t) return { title: '', subtitle: '', overflow: '' };
  const nl = t.indexOf('\n');
  const firstLine = nl > -1 ? t.slice(0, nl).trim() : t;
  const rest = nl > -1 ? t.slice(nl + 1).trim() : '';

  let title = firstLine;
  let subtitle = '';
  for (const d of [' — ', ' – ', ' -- ', ' - ']) {
    const idx = firstLine.indexOf(d);
    if (idx > -1) {
      title = firstLine.slice(0, idx).trim();
      subtitle = firstLine.slice(idx + d.length).trim();
      break;
    }
  }
  if (!subtitle && wordCount(firstLine) > TITLE_MAX_WORDS) {
    const sentence = splitFirstSentence(firstLine);
    if (sentence && wordCount(sentence.head) <= TITLE_MAX_WORDS) {
      title = sentence.head;
      subtitle = sentence.rest;
    }
  }
  if (subtitle) return { title, subtitle, overflow: rest };
  // "Title\nSubtitle": the next line is the subtitle, anything after it overflows.
  const nextBreak = rest.indexOf('\n');
  return nextBreak > -1
    ? { title, subtitle: rest.slice(0, nextBreak).trim(), overflow: rest.slice(nextBreak + 1).trim() }
    : { title, subtitle: rest, overflow: '' };
}

/**
 * Fit a cover/title split to the slide's word cap: if title + subtitle runs
 * over, the subtitle joins the overflow, which goes onto continuation
 * slides rather than off the edge of the cover.
 */
function fitCover(text: string, cap: number | undefined): { title: string; subtitle: string; overflow: string } {
  const split = splitCoverTitle(text);
  if (!cap) {
    // No cap: keep everything on the slide, as before.
    const subtitle = [split.subtitle, split.overflow].filter(Boolean).join('\n\n');
    return { title: split.title, subtitle, overflow: '' };
  }
  if (wordCount(split.title) + wordCount(split.subtitle) > cap) {
    return {
      title: split.title,
      subtitle: '',
      overflow: [split.subtitle, split.overflow].filter(Boolean).join('\n\n'),
    };
  }
  return split;
}

function bulletsToHtml(text: string): string {
  const t = (text || '').trim();
  if (!t) return '<ul><li></li></ul>';
  const items = t
    .split(/\n|;/)
    .map((s) => s.replace(/^\s*[-*•]\s*/, '').trim())
    .filter(Boolean);
  if (items.length === 0) return '<ul><li></li></ul>';
  return `<ul>${items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
}

function splitTwoColumn(text: string): { left: string; right: string } {
  const t = (text || '').trim();
  for (const d of ['||', '//', '|']) {
    const idx = t.indexOf(d);
    if (idx > -1) {
      return { left: t.slice(0, idx).trim(), right: t.slice(idx + d.length).trim() };
    }
  }
  return { left: t, right: '' };
}

function isUrl(text: string): boolean {
  return /^https?:\/\//i.test((text || '').trim());
}

/** Pull `src="..."` from an `<iframe ...>` embed snippet. Returns null if no src. */
function extractIframeSrc(text: string): string | null {
  const m = (text || '').match(/<iframe[^>]*\bsrc=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

/** Trimmed `imageUrl` field if present, else null. Used by image-bearing importers. */
function readImageUrl(ds: { imageUrl?: string | null }): string {
  return (ds.imageUrl || '').trim();
}

function optionText(o: any): string {
  if (typeof o === 'string') return o;
  return String(o?.text ?? o);
}

function mapTextOptions(opts: any[]): Array<{ id: string; text: string }> {
  return opts.map((o) => ({ id: crypto.randomUUID(), text: optionText(o) }));
}

// --- Slide builders ---

// Cover, title, text and image+text importers take the registry's word cap
// and return MORE than one slide when the text won't fit: the first slide
// keeps the title, the rest carry the overflow untitled. Text past the cap
// used to land on the slide anyway, under the fold, with only an advisory
// warning in the import toast.

export function buildCover(
  ds: DesignerSlide,
  _warnings?: string[],
  limits?: ImportLimits,
): CoverSlideData | SlideData[] {
  const { title, subtitle, overflow } = fitCover(ds.text, limits?.maxWords);
  const cover: CoverSlideData = {
    template: 'cover',
    title: title || 'Cover',
    subtitle,
    logoUrl: '',
    layout: 'centered',
    showAccentBar: true,
    darkOverlay: false,
  };
  return oneOrMany<SlideData>([cover, ...continuationSlides(overflow)]) as CoverSlideData | SlideData[];
}

export function buildTitle(
  ds: DesignerSlide,
  _warnings?: string[],
  limits?: ImportLimits,
): TextSlideData | TextSlideData[] {
  const { title, subtitle, overflow } = fitCover(ds.text, limits?.maxWords);
  return oneOrMany<TextSlideData>([
    { template: 'text', layout: 'title', heading: title, body: subtitle, alignment: 'center' },
    ...continuationSlides(overflow),
  ]);
}

/** Heading + body paginated to the cap; only the first page keeps the title. */
function textPages(text: string, cap: number | undefined): { heading: string; pages: string[] } {
  const { heading, body } = splitHeadingBody(text);
  const pages = cap ? paginateBody(body, cap - wordCount(heading)) : [body];
  return { heading, pages };
}

export function buildText(
  ds: DesignerSlide,
  _warnings?: string[],
  limits?: ImportLimits,
): TextSlideData | TextSlideData[] {
  const { heading, pages } = textPages(ds.text, limits?.maxWords);
  return oneOrMany<TextSlideData>(
    pages.map((page, i) => ({
      template: 'text',
      layout: 'body',
      heading: i === 0 ? heading : '',
      body: wrapParagraph(page),
    })),
  );
}

export function buildTextImage(
  ds: DesignerSlide,
  _warnings?: string[],
  limits?: ImportLimits,
): TextSlideData | TextSlideData[] {
  const { heading, pages } = textPages(ds.text, limits?.maxWords);
  // The image stays with the title; continuation pages are plain text, paged
  // to the image slide's (smaller) cap so they read at the same density.
  return oneOrMany<TextSlideData>(
    pages.map((page, i) =>
      i === 0
        ? {
            template: 'text',
            layout: 'image-text',
            heading,
            body: wrapParagraph(page),
            imageUrl: readImageUrl(ds),
            imagePosition: 'right',
            imageSize: 'large',
          }
        : { template: 'text', layout: 'body', heading: '', body: wrapParagraph(page) },
    ),
  );
}

export function buildBullets(ds: DesignerSlide): TextSlideData {
  const t = (ds.text || '').trim();
  let heading = '';
  let rest = t;
  const nl = t.indexOf('\n');
  if (nl > -1) {
    heading = t.slice(0, nl).trim();
    rest = t.slice(nl + 1).trim();
  }
  return {
    template: 'text',
    layout: 'bullets',
    heading,
    body: bulletsToHtml(rest),
  };
}

export function buildTwoColumn(ds: DesignerSlide): TextSlideData {
  const { left, right } = splitTwoColumn(ds.text);
  return {
    template: 'text',
    layout: 'two-column',
    body: wrapParagraph(left),
    secondaryBody: wrapParagraph(right),
    ratio: '50-50',
  };
}

export function buildMedia(ds: DesignerSlide): ImageSlideData {
  return {
    template: 'image',
    url: readImageUrl(ds),
    caption: (ds.text || '').trim(),
  };
}

export function buildVideo(ds: DesignerSlide): VideoSlideData {
  // Designer can supply the video URL either in `imageUrl` (preferred,
  // structured) or inline in `text` if it starts with http(s).
  const explicit = readImageUrl(ds);
  const text = (ds.text || '').trim();
  const url = explicit || (isUrl(text) ? text : '');
  const caption = explicit ? text : isUrl(text) ? '' : text;
  return {
    template: 'video',
    url,
    caption,
  };
}

export function buildCode(ds: DesignerSlide): CodeSlideData {
  return {
    template: 'code',
    heading: '',
    code: ds.text || '',
    language: (ds.language || '').trim() || 'javascript',
    showLineNumbers: true,
  };
}

export function buildQuiz(ds: DesignerSlide, warnings: string[]): QuizSlideData {
  const rawOptions = Array.isArray(ds.options) ? ds.options : [];
  if (rawOptions.length === 0) {
    warnings.push(`Slide ${ds.n}: QUIZ is missing options.`);
  }
  return {
    template: 'quiz',
    question: (ds.text || '').trim(),
    options: rawOptions.map((o: any) => ({
      id: crypto.randomUUID(),
      text: typeof o?.text === 'string' ? o.text : String(o?.text ?? ''),
      isCorrect: Boolean(o?.correct),
      feedback: typeof o?.feedback === 'string' ? o.feedback : '',
    })),
    explanation: '',
  };
}

export function buildLikert(ds: DesignerSlide, warnings: string[]): LikertSlideData {
  const opts = Array.isArray(ds.options) ? ds.options : [];
  if (opts.length < 2) {
    warnings.push(`Slide ${ds.n}: LIKERT expects 2 scale labels.`);
  }
  return {
    template: 'activity',
    activityType: 'likert',
    mode: 'survey',
    question: (ds.text || '').trim(),
    scaleLabels: {
      low: typeof opts[0] === 'string' ? opts[0] : 'Strongly Disagree',
      high: typeof opts[1] === 'string' ? opts[1] : 'Strongly Agree',
    },
    scaleSize: 5,
  };
}

export function buildSurvey(ds: DesignerSlide, warnings: string[]): SurveyChoiceSlideData {
  const opts = Array.isArray(ds.options) ? ds.options : [];
  if (opts.length === 0) warnings.push(`Slide ${ds.n}: SURVEY is missing options.`);
  // Prefer the explicit `multiple` field. Fall back to the text-sniffing
  // heuristic only when the field is absent — keeps old Designer outputs working.
  const allowMultiple =
    typeof ds.multiple === 'boolean'
      ? ds.multiple
      : /select all|choose multiple|all that apply/.test((ds.text || '').toLowerCase());
  return {
    template: 'activity',
    activityType: 'survey-choice',
    mode: 'survey',
    question: (ds.text || '').trim(),
    options: mapTextOptions(opts),
    allowMultiple,
  };
}

export function buildFreeText(ds: DesignerSlide): FreeTextSlideData {
  return {
    template: 'activity',
    activityType: 'free-text',
    mode: 'survey',
    question: (ds.text || '').trim(),
    placeholder: 'Type your response...',
  };
}

export function buildDropdown(ds: DesignerSlide, warnings: string[]): DropdownSlideData {
  const opts = Array.isArray(ds.options) ? ds.options : [];
  if (opts.length === 0) warnings.push(`Slide ${ds.n}: DROPDOWN is missing options.`);
  return {
    template: 'activity',
    activityType: 'dropdown',
    mode: 'survey',
    question: (ds.text || '').trim(),
    options: mapTextOptions(opts).map((o) => ({ ...o, isCorrect: false })),
  };
}

export function buildRanking(ds: DesignerSlide, warnings: string[]): RankingSlideData {
  const opts = Array.isArray(ds.options) ? ds.options : [];
  if (opts.length === 0) warnings.push(`Slide ${ds.n}: RANKING is missing items.`);
  return {
    template: 'activity',
    activityType: 'ranking',
    mode: 'survey',
    question: (ds.text || '').trim(),
    items: mapTextOptions(opts),
  };
}

export function buildMatching(ds: DesignerSlide, warnings: string[]): MatchingSlideData {
  const pairs = Array.isArray(ds.pairs) ? ds.pairs : [];
  if (pairs.length === 0) warnings.push(`Slide ${ds.n}: MATCH is missing pairs.`);
  return {
    template: 'activity',
    activityType: 'matching',
    mode: 'quiz',
    question: (ds.text || '').trim(),
    pairs: pairs
      .filter((p: any) => Array.isArray(p) && p.length >= 2)
      .map((p: any) => ({
        id: crypto.randomUUID(),
        left: String(p[0] ?? ''),
        right: String(p[1] ?? ''),
      })),
  };
}

export function buildReflection(ds: DesignerSlide): ReflectionSlideData {
  const t = (ds.text || '').trim();
  let parts = t.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) {
    parts = t
      .split(/\n/)
      .map((s) => s.replace(/^\s*\d+[.)]\s*/, '').trim())
      .filter(Boolean);
  }
  if (parts.length === 0) parts = [''];
  return {
    template: 'reflection',
    heading: '',
    description: '',
    questions: parts.map((prompt) => ({ id: crypto.randomUUID(), prompt })),
  };
}

export function buildWebapp(ds: DesignerSlide): WebappSlideData {
  const t = (ds.text || '').trim();
  return {
    template: 'webapp',
    url: isUrl(t) ? t : '',
    title: isUrl(t) ? '' : t,
  };
}

export function buildCanva(ds: DesignerSlide): CanvaSlideData {
  const t = (ds.text || '').trim();
  // Accept three forms: bare URL, full <iframe> embed code, or a plain title.
  const iframeSrc = extractIframeSrc(t);
  const url = iframeSrc || (isUrl(t) ? t : '');
  return {
    template: 'canva',
    embedUrl: url,
    title: url ? '' : t,
  };
}

/**
 * DOOR slot → briefed `generated-interactive` slide. Lands briefed, NOT built
 * (KULA-DOORS-SKETCH-PLAN.md decision 4): each door build is a real credit
 * spend, so the author triggers it from the slide's stub — deliberately unlike
 * the picker path, where intake submit builds immediately. useLessonSource
 * defaults ON: the slides the Designer wrote around this slot ARE the source.
 */
export function buildDoorSlot(ds: DesignerSlide, warnings: string[]): GeneratedInteractiveSlideData {
  const rawDoor = (ds.door || '').trim();
  const isKnown = DOORS.some((d) => d.key === rawDoor);
  if (!isKnown) {
    warnings.push(
      `Slide ${ds.n}: Door slide with ${rawDoor ? `unknown door "${rawDoor}"` : 'no door named'} — imported as a Scenario door. Check the brief before building.`,
    );
  }
  const door = (isKnown ? rawDoor : 'scenario') as DoorKey;
  return {
    template: 'generated-interactive',
    door,
    intake: {
      context: htmlToPlain(ds.text || ''),
      useLessonSource: true,
      goal: (ds.goal || '').trim(),
      length: (ds.length || '').trim() === 'long' ? 'long' : 'short',
      world: getDoor(door).defaultWorld,
    },
  };
}

export function buildNotes(ds: DesignerSlide): string | undefined {
  const parts: string[] = [];
  if (ds.note && ds.note.trim()) parts.push(ds.note.trim());
  if (ds.image && ds.image.trim()) parts.push(`[Image needed: ${ds.image.trim()}]`);
  return parts.length ? parts.join('\n\n') : undefined;
}

// Type alias re-export so consumers can grab SlideData from this module too.
export type { SlideData };
